import React from 'react';
import { State, OnActionArgs, OnEnterArgs } from '..';
import { addNotification, INotificationContext } from 'utils/NotificationManager';
import { KeysOfType } from 'component_utils/typescriptUtils';
import Translation from 'utils/Language/Translation';
import { isArray, isDate } from 'lodash';
import { ScannerInputModes } from 'components/Scanner';
import moment from 'moment';

type Find<S, P, R> = (state: OnActionArgs<S, P, R>) => Promise<[string, string]|Date>;
type PreFind<S, P, R> = (state: OnEnterArgs<S, P, R>) => Promise<[string, string]|Date>;

interface EventProps {
  notifications: INotificationContext;
}

interface Config<S, P, A, R> {
  nextState: number;
  targetKey: KeysOfType<S, Date>;
  attributes?: Partial<A>;
  find?: Find<S, P, R>;
  preFind?: PreFind<S, P, R>;
}

const StateFindDate = function <S, P extends EventProps, A extends { hint: string; inputType: ScannerInputModes; }, R>({
  nextState,
  targetKey,
  find,
  preFind,
  attributes = {},
}: Config<S, P, A, R>): State<S, P, A, R> {
  return {
    onEnter: async (args) => {
      if (preFind) {
        const found = await preFind(args);
        if (found !== undefined) {
          let result = null
          if (isArray(found)) {
            const [f, v] = found
            if (v !== "") {
              // try to parse the string
              const m = moment.utc(v, f, true)
              if (!m.isValid()) {
                throw new Error(`${v} is not a valid date string`)
              }
              result = m.toDate()
            }
          } else if (isDate(found)) {
            result = moment(found).utc(true).toDate()
          } else {
            result = found
          }
    
          return {
            type: 'MOVE',
            data: {
              [targetKey]: result,
            } as any,
            nextState,
          };
        }
      }
    },
    onAction: async (args) => {
      const found = await find(args);
      if (found === undefined) {
        throw args.props.notifications.add('danger', <Translation name="T.errors.notFound" />);
      }

      let result = null
      if (isArray(found)) {
        const [f, v] = found
        if (v !== "") {
          // try to parse the string
          const m = moment.utc(v, f, true)
          if (!m.isValid()) {
            addNotification('danger', <Translation name="T.misc.incorrectFormat"/>)
            throw new Error(`${found} is not a valid date string`)
          }
          result = m.toDate()
        }
      } else if (isDate(found)) {
        result = moment(found).utc(true).toDate()
      } else {
        result = found
      }

      return {
        type: 'MOVE',
        data: {
          [targetKey]: result,
        } as any,
        nextState,
      };
    },
    attributes: {
      hint: 'T.hints.date',
      inputType: 'date' as ScannerInputModes,
      ...(attributes as any),
    },

    nodeName: 'T.states.findDate',
    outputs: [
      {
        name: 'onScan',
        to: nextState,
      },
    ],
  };
};

export default StateFindDate;
